import type { ReactNode } from "react";
import { createContext, useReducer, use } from "react";

type LockState = {
  /* example: { 
        '1-2-3-4': ['ADMIN', 'BC1'],
        '4-3-2-1': ['user@example.com', 'BC2'],
    }
    */
  [uuid in string]: [string, string, boolean];
};
type Action = {
  type: /* Add a new lock */
    | "add"
    /* Remove a single lock */
    | "remove"
    /* Update all locks at once */
    | "set"
    /* Like 'set', but tries to avoid a refresh if state is unchanged */
    | "merge";
  data: { [uuid in string]: [string, string, boolean] };
};

type Children = { children?: ReactNode };

function LockReducer(state: LockState, action: Action): LockState {
  let newState: LockState;
  switch (action.type) {
    // add new Locks, merge state
    case "add": {
      newState = {
        ...state,
        ...action.data,
      };
      break;
    }
    // remove a Lock, merge state
    case "remove": {
      newState = { ...state };
      for (const key of Object.keys(action.data)) delete newState[key];
      break;
    }
    // override state if it's changed
    case "set": {
      newState = { ...action.data };
      break;
    }
    case "merge": {
      newState = { ...action.data };
      let noNewLocks =
        Object.keys(newState).length == Object.keys(state).length;
      const newStateKeys = Object.keys(newState);
      for (let i = 0; noNewLocks && i < newStateKeys.length; ++i) {
        const lockedUuid = newStateKeys[i];
        if (state[lockedUuid] == undefined) {
          noNewLocks = false;
        } else {
          const newLock = newState[lockedUuid];
          const otherLock = state[lockedUuid];
          if (
            newLock[0] !== otherLock[0] ||
            newLock[1] !== otherLock[1] ||
            newLock[2] !== otherLock[2]
          ) {
            noNewLocks = false;
          }
        }
      }
      if (noNewLocks) {
        // successfully avoided refresh
        newState = state;
      }
      break;
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
  return newState;
}

const LockedBatchStateContext = createContext<LockState>({});
const LockedBatchDispatchContext = createContext<React.Dispatch<Action>>(
  () => null,
);

/**
 *
 */
const LockedBatchProvider = ({ children }: Children) => {
  const [state, dispatch] = useReducer(LockReducer, {});

  return (
    <LockedBatchStateContext value={state}>
      <LockedBatchDispatchContext value={dispatch}>
        {children}
      </LockedBatchDispatchContext>
    </LockedBatchStateContext>
  );
};

function useBatchLockState(): LockState {
  const context = use(LockedBatchStateContext);
  if (context === undefined) {
    throw new Error(
      "useBatchLockState must be used within a LockedBatchProvider",
    );
  }
  return context;
}
function useBatchLockDispatch() {
  const context = use(LockedBatchDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useLockDispatch must be used within a LockedBatchProvider",
    );
  }
  return context;
}

const LockedModelStateContext = createContext<LockState>({});
const LockedModelDispatchContext = createContext<React.Dispatch<Action>>(
  () => null,
);
/**
 *
 */
const LockedModelProvider = ({ children }: Children) => {
  const [state, dispatch] = useReducer(LockReducer, {});

  return (
    <LockedModelStateContext value={state}>
      <LockedModelDispatchContext value={dispatch}>
        {children}
      </LockedModelDispatchContext>
    </LockedModelStateContext>
  );
};

function useModelLockState(): LockState {
  const context = use(LockedModelStateContext);
  if (context === undefined) {
    throw new Error(
      "useBatchLockState must be used within a LockedModelStateContext",
    );
  }
  return context;
}
function useModelLockDispatch() {
  const context = use(LockedModelDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useLockDispatch must be used within a LockedBatchProvider",
    );
  }
  return context;
}

const LockedBatchClassStateContext = createContext<LockState>({});
const LockedBatchClassDispatchContext = createContext<React.Dispatch<Action>>(
  () => null,
);
/**
 *
 */
const LockedBatchClassProvider = ({ children }: Children) => {
  const [state, dispatch] = useReducer(LockReducer, {});

  return (
    <LockedBatchClassStateContext value={state}>
      <LockedBatchClassDispatchContext value={dispatch}>
        {children}
      </LockedBatchClassDispatchContext>
    </LockedBatchClassStateContext>
  );
};

function useBatchClassLockState(): LockState {
  const context = use(LockedBatchClassStateContext);
  if (context === undefined) {
    throw new Error(
      "useBatchLockState must be used within a LockedBatchClassStateContext",
    );
  }
  return context;
}
function useBatchClassLockDispatch() {
  const context = use(LockedBatchClassDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useLockDispatch must be used within a LockedBatchProvider",
    );
  }
  return context;
}

export { LockState };
/** Exported Providers */
export { LockedBatchProvider, LockedModelProvider, LockedBatchClassProvider };
/** Exported for CrudModelConfiguration  */
export { LockedModelStateContext };
/** Exported useBatchLock* methods */
export { useBatchLockState, useBatchLockDispatch };
/** Exported useeModelLock* methods */
export { useModelLockState, useModelLockDispatch };
/** Exported useBatchClassLock* methods */
export { useBatchClassLockState, useBatchClassLockDispatch };
